import { formaterTaille } from '../../../../../packages/ui/src/format.js';

/** Paramètres de la photo de pièce : donnée versionnée (cible de 500 Ko : CA-HER-04a), jamais du code en dur. */
export const PARAMETRES_PHOTO = {
  version: '2026-10-17',
  cibleOctets: 500 * 1024,
  largeurMax: 1600,
  qualiteInitiale: 0.85,
  qualiteMin: 0.4,
  pas: 0.1,
  /** Marge autour du cadre de prise de vue, en part de l'écran. */
  margeCadre: 0.06,
} as const;

export interface PhotoBrute {
  octets: Uint8Array;
  mime: string;
  largeur: number;
  hauteur: number;
}

/** Encodeur du téléphone (canvas, en production) : réencode l'image à une largeur et une qualité données. */
export type Encodeur = (entree: Uint8Array, parametres: { largeurMax: number; qualite: number }) => Promise<Uint8Array>;

export interface PhotoCompressee {
  octets: Uint8Array;
  avant: number;
  apres: number;
  qualite: number;
  /** Faux quand même la qualité minimale ne tient pas sous la cible : la meilleure version obtenue est rendue. */
  reussi: boolean;
}

export async function compresserPhoto(photo: PhotoBrute, encoder: Encodeur): Promise<PhotoCompressee> {
  const { cibleOctets, largeurMax, qualiteInitiale, qualiteMin, pas } = PARAMETRES_PHOTO;
  const avant = photo.octets.length;
  if (avant <= cibleOctets) return { octets: photo.octets, avant, apres: avant, qualite: 1, reussi: true };
  let meilleur: Uint8Array = photo.octets;
  let qualite: number = qualiteInitiale;
  for (let q: number = qualiteInitiale; q >= qualiteMin; q = Math.round((q - pas) * 100) / 100) {
    const sortie = await encoder(photo.octets, { largeurMax: Math.min(photo.largeur, largeurMax), qualite: q });
    meilleur = sortie;
    qualite = q;
    if (sortie.length <= cibleOctets) break;
  }
  return { octets: meilleur, avant, apres: meilleur.length, qualite, reussi: meilleur.length <= cibleOctets };
}

export const resumeCompression = ({ avant, apres }: { avant: number; apres: number }): string =>
  `Avant : ${formaterTaille(avant)}. Après : ${formaterTaille(apres)}.`;

export interface Cadre {
  x: number;
  y: number;
  largeur: number;
  hauteur: number;
}

/** Cadre de prise de vue : une feuille portrait (1 : √2) centrée, avec une marge, entièrement dans l'écran. */
export function cadreDocument(largeurEcran: number, hauteurEcran: number): Cadre {
  const dispo = 1 - 2 * PARAMETRES_PHOTO.margeCadre;
  const largeur = Math.min(largeurEcran * dispo, (hauteurEcran * dispo) / Math.SQRT2);
  const hauteur = largeur * Math.SQRT2;
  return { x: (largeurEcran - largeur) / 2, y: (hauteurEcran - hauteur) / 2, largeur, hauteur };
}
